import { normalizeWatchSymbols } from "@shared/watchlist";

const STORAGE_KEY = "fahali_pending_watch_v1";

export interface PendingWatchIntent {
  symbols: string[];
  source: string;
  capturedAt: string;
}

let locationCaptureAttempted = false;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readStoredIntent(): PendingWatchIntent | null {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingWatchIntent>;
    const symbols = normalizeWatchSymbols(parsed.symbols);
    if (!symbols.length) return null;
    return {
      symbols,
      source: String(parsed.source || "app-watch").slice(0, 64),
      capturedAt: String(parsed.capturedAt || new Date().toISOString()),
    };
  } catch {
    return null;
  }
}

export function setPendingWatchIntent(
  rawSymbols: unknown,
  source: string = "app-watch",
): PendingWatchIntent | null {
  const symbols = normalizeWatchSymbols(rawSymbols);
  if (!symbols.length) return null;
  const intent: PendingWatchIntent = {
    symbols,
    source: String(source || "app-watch").replace(/[^a-z0-9_.-]/gi, "-").slice(0, 64),
    capturedAt: new Date().toISOString(),
  };
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // The in-memory caller can still continue even when private browsing blocks storage.
  }
  return intent;
}

/** Capture the landing-page handoff before navigation or auth rewrites the URL. */
export function captureWatchIntentFromLocation(): PendingWatchIntent | null {
  if (locationCaptureAttempted) return readStoredIntent();
  locationCaptureAttempted = true;
  if (typeof window === "undefined") return readStoredIntent();

  try {
    const url = new URL(window.location.href);
    const isWatchPath = url.pathname.replace(/\/+$/, "") === "/watch";
    const isLegacyWatch = url.searchParams.get("intent") === "watch";
    if (!isWatchPath && !isLegacyWatch) return readStoredIntent();
    const source = url.searchParams.get("source") || "landing-result";
    return setPendingWatchIntent(url.searchParams.get("symbols") || "", source);
  } catch {
    return readStoredIntent();
  }
}

export function getPendingWatchIntent(): PendingWatchIntent | null {
  captureWatchIntentFromLocation();
  return readStoredIntent();
}

export function hasPendingWatchIntent(): boolean {
  return Boolean(getPendingWatchIntent()?.symbols.length);
}

export function clearPendingWatchIntent(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing else depends on cleanup succeeding.
  }
}
